import * as React from "react";
import { Section, Stack } from "@origin-digital/ods-core";
import { useNavigate, useParams } from "react-router-dom";
import { logger } from "@origin-digital/reporting-client";
import { IAppContext } from "../AppContext";
import { SubCategoryListController } from "../components/SubCategoryList";
import { routes } from "../helpers/router.helper";
import { UseParamsType } from "../router";
import { useDaxi } from "../services/useDaxi";
import { ISubcategoryAPIResponse } from "../../../shared/interface/subcategory";
import { ContentHeader } from "../components/SearchBanner/ContentHeader";
import { PathnameMap } from "../interfaces/breadcrumbs";
import {
  BreadcrumbBaseUri,
  BreadrumbBaseName,
} from "../components/Breadcrumbs/Breadcrumbs.controller";
import { IBreadcrumb } from "../../../shared/interface/breadcrumb";
import { getSubcategoryAPIPath } from "../services/paths";
import metaContent from "../cms/meta.json";
import { ScreenTemplate } from "./ScreenTemplate";

interface IProps {
  context: IAppContext;
}

export const Subcategory = ({ context }: IProps) => {
  const { isNative } = context;

  const { category, subcategory } = useParams() as UseParamsType;
  const navigate = useNavigate();

  const subcategoryRes = useDaxi<ISubcategoryAPIResponse>(
    getSubcategoryAPIPath(category, subcategory),
    {
      xhr: true,
    }
  );
  const daxiError = subcategoryRes.error;
  const data = subcategoryRes.data;

  if (daxiError || (data !== undefined && Object.entries(data).length === 0)) {
    logger.warn(`[SubCategory] cannot find subcategory: "${subcategory}"`);
    navigate(routes.category(category), { replace: true });
  }
  const title = data?.subcategory?.name || "";
  const articles = data?.articles || [];
  const breadcrumbs = data?.breadcrumbs || [];
  const metadata = data?.metadata;
  const description = subcategoryRes.loading
    ? ""
    : metadata?.description ||
      data?.subcategory?.description ||
      metaContent.defaultDescription;

  return (
    <ScreenTemplate data-id="subcategory" metadata={{ title, description }}>
      <Section backgroundColor="transparent">
        <ContentHeader
          title={title}
          description={description}
          isNative={isNative}
          hideTitleAndDescription={false}
          hideBreadcrumbs={false}
          titleIsHeading={true}
          breadcrumbsMap={BreadcrumbMap(breadcrumbs)}
        />
      </Section>
      <Section backgroundColor="white">
        <Stack space="xxlarge">
          <SubCategoryListController
            articles={articles}
            categorySlug={category}
            subcategorySlug={subcategory}
            data-id="subcategory-page-article-list"
          />
        </Stack>
      </Section>
    </ScreenTemplate>
  );
};

const BreadcrumbMap = (breadcrumbs: IBreadcrumb[]): PathnameMap => {
  const breadcrumbMap = { [BreadcrumbBaseUri]: BreadrumbBaseName };
  if (breadcrumbs && breadcrumbs.length === 2) {
    breadcrumbMap[`${BreadcrumbBaseUri}/${breadcrumbs[0].uri}`] =
      breadcrumbs[0].text;
    breadcrumbMap[
      `${BreadcrumbBaseUri}/${breadcrumbs[0].uri}/${breadcrumbs[1].uri}`
    ] = breadcrumbs[1].text;
  }
  return breadcrumbMap;
};
